import { Classes, Dialog, Button, Intent, Callout } from "@blueprintjs/core";
import { FormikProps } from "formik";
import React, { useCallback, useRef, useState,useEffect } from "react";
import { AvailableTemplate } from "src/models/AvailableTemplates";
import { FormInput } from "src/models/UserInputs";
import { createProcessRequestInput, Dictionary } from "src/utils/utils";
import SetInstructions from "./SetInstructions";
import { RunToaster } from "../../components/Toaster";
import { useSendProcessMessage } from "src/hooks/useSendMessage";
import TemplateRunningView from "./TemplateRunningView";

import { useTemplateHistory } from "src/hooks/useTemplateHistory";

type Props = {
    template: AvailableTemplate;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function CreateInstructionRunDiaglog({ template, isOpen, setIsOpen }: Props) {
    const formRef = useRef<FormikProps<Dictionary<FormInput>>>();
    const [submitting, setSubmitting] = useState(false);

    const [runningDialogOpen, setRunningDialogOpen] = useState(false);
    const [runInfoLoading, setRunInfoLoading] = useState(false);

    const [requestSysId, setRequestSysId] = useState<string | undefined | null>();
    const [lastRun, runLoading, runError, refetchLastRun] = useTemplateHistory(template ? template.processId : undefined);
    const [runStarting, setRunStarting] = useState(false);
    useEffect(() => {
        if (lastRun) {
            setRequestSysId(lastRun.requestSysId);
        }
    }, [lastRun]);
    const {
        processMassageMutation: [createRun]
    } = useSendProcessMessage();

    const handleRunningClose = useCallback(() => {
        setRunningDialogOpen(false);
    }, []);

    const handleCreateDialogClose = useCallback(() => {
        setIsOpen(false);
    }, [setIsOpen]);

    const handleSubmit = useCallback(() => {
        if (formRef.current) {
            const invalid = invalidForm(formRef.current.values);
            if (invalid) {
                RunToaster.show({
                    message: `Could not run template. Please fill out all required fields.`,
                    intent: Intent.DANGER
                });
                return;
            }
            const processInputs = formRef.current.values;
            const requestInput = createProcessRequestInput(processInputs, template, "run");
            console.log("request Input: ", requestInput);

            setSubmitting(true);
            setRunInfoLoading(true);
            setRunStarting(true);
            createRun({ variables: { input: requestInput } })
                .then(() => {
                    RunToaster.show({
                        message: `Successfully started running template: ${template.name}`,
                        intent: Intent.SUCCESS,
                        // action: {
                        //     onClick: () => console.log("well hello there")
                        // }
                    });
                    // setRunningDialogOpen(true);
                    
                    setRequestSysId(requestInput.requestSysId);
                    refetchLastRun();
                })
                .catch(err => {
                    RunToaster.show({
                        message: `Failed to run template: ${err}.`,
                        intent: Intent.WARNING
                    });
                })
                .finally(() => {
                    // setIsOpen(false);
                    setSubmitting(false);
                    setRunInfoLoading(false);
                });
        }
    }, [createRun, refetchLastRun, template]);

    return (
        <>
            <Dialog
                enforceFocus={false}
                icon="annotation"
                title={`${template ? template.name : "Unknown Template Name"}`}
                isOpen={isOpen}
                onClose={handleCreateDialogClose}
            >
                <div className={Classes.DIALOG_BODY}>
                    {template?.instructions.length > 0 ? (
                        <SetInstructions template={template} formRef={formRef} />
                    ) : (
                        <Callout title="Template Has No Instructions" />
                    )}
                </div>
                <div className={Classes.DIALOG_FOOTER}>
                    <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                        <Button
                            type="submit"
                            text="Run"
                            intent={Intent.PRIMARY}
                            disabled={false}
                            onClick={handleSubmit}
                            loading={submitting}
                        />
                        <Button
                            disabled={!lastRun || !!runError}
                            loading={!!runLoading || runStarting}
                            intent={Intent.SUCCESS}
                            text="Last Run"
                            onClick={() => {
                                setRunningDialogOpen(true);
                            }}
                        />
                        <Button
                            text="Close"
                            onClick={() => {
                                setIsOpen(false);
                                setRequestSysId(undefined);
                            }}
                        />
                    </div>
                </div>
            </Dialog>

            {template && requestSysId && (
                <TemplateRunningView
                    name={template.name ? template.name : "No Name Template"}
                    processId={template.processId}
                    companySysId={template.companySysId}
                    requestSysId={requestSysId}
                    instructions={template.instructions as any}
                    isOpen={runningDialogOpen}
                    onClose={handleRunningClose}
                    lastRun={lastRun}
                    refetchLastRun={refetchLastRun}
                    setRunStarting={setRunStarting}
                />
            )}
        </>
    );
}

function invalidForm(values: Dictionary<FormInput>) {
    for (const field of Object.values(values)) {
        if (field.required) {
            if (Array.isArray(field.value) && field.value.length === 0) {
                return true;
            } else if (field.value === "") {
                return true;
            }
        }
    }
    return false;
}
